import React, { useCallback, useEffect, useRef } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { platformAlert } from '../utils/platformAlert';
import Toast from 'react-native-toast-message';
import { useAuth } from '../hooks/useAuth';
import { useExpoPushNotifications } from '../hooks/useExpoPushNotifications';
import NavigationService from '../services/NavigationService';
import PushNotificationsManager from './PushNotificationsManager';

// Platform-specific imports
let Notifications: any = null;
let PermissionStatus: any = { GRANTED: 'granted', DENIED: 'denied', UNDETERMINED: 'undetermined' };

if (Platform.OS !== 'web') {
  try {
    Notifications = require('expo-notifications');
    const expoModulesCore = require('expo-modules-core');
    PermissionStatus = expoModulesCore.PermissionStatus;
  } catch (e) {
    console.warn('[NotificationHandler] expo-notifications not available');
  }
}

interface NotificationHandlerProps {
  children: React.ReactNode;
}

const PROMPT_COOLDOWN_MS = 1000 * 60 * 10; // 10 minutes

// Debug logging helper for notification handler
const logNotificationHandler = (context: string, data: Record<string, unknown>) => {
  const prefix = `🔔 [NotificationHandler][${Platform.OS.toUpperCase()}]`;
  console.log(`${prefix} ${context}:`, JSON.stringify(data, null, 2));
};

function normalizeData(raw: any): Record<string, any> {
  if (!raw) {
    return {};
  }

  // Convert stringified JSON payloads if present
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch {
      return { value: raw };
    }
  }

  return raw as Record<string, any>;
}

function ensureString(value: any): string | undefined {
  if (value === null || value === undefined) return undefined;
  return String(value);
}

function navigateFromNotification(data: Record<string, any>) {
  const type = ensureString(data.type ?? data.notification_type);

  switch (type) {
    case 'chat_message': {
      const chatId = ensureString(data.chatId ?? data.chat_id ?? data.chatID);
      if (chatId) {
        NavigationService.navigate('Chat' as any, { id: chatId } as any);
      } else {
        NavigationService.navigate('Notifications' as any);
      }
      break;
    }
    case 'seller_confirmation':
    case 'system_announcement':
    case 'promotional': {
      NavigationService.navigate('Notifications' as any);
      break;
    }
    case 'delivery_update': {
      const deliveryId = ensureString(data.deliveryId ?? data.delivery_id);
      const orderId = ensureString(data.orderId ?? data.order_id);
      if (deliveryId) {
        NavigationService.navigate('OrderTracking' as any, { deliveryId, orderId } as any);
      } else {
        NavigationService.navigate('Orders' as any);
      }
      break;
    }
    case 'order_status':
    case 'payment_update': {
      NavigationService.navigate('Orders' as any);
      break;
    }
    case 'new_review': {
      const vendorOrderId = ensureString(data.vendorOrderId ?? data.vendor_order_id);
      if (vendorOrderId) {
        NavigationService.navigate('VendorOrderDetails' as any, { vendorOrderId } as any);
      } else {
        NavigationService.navigate('Orders' as any);
      }
      break;
    }
    case 'rate_purchase': {
      const orderId = ensureString(data.orderId ?? data.order_id);
      if (orderId) {
        NavigationService.navigate('Orders' as any);
      } else {
        NavigationService.navigate('Orders' as any);
      }
      break;
    }
    default: {
      NavigationService.navigate('Notifications' as any);
    }
  }
}

// Platform-aware alert function
const showAlert = (title: string, message: string, buttons?: Array<{ text: string; onPress?: () => void; style?: string }>, options?: { cancelable?: boolean }) => {
  if (Platform.OS === 'web') {
    const confirmed = window.confirm(`${title}\n\n${message}`);
    if (confirmed && buttons?.[buttons.length - 1]?.onPress) {
      buttons[buttons.length - 1].onPress!();
    }
  } else {
    platformAlert(title, message, buttons as any, options);
  }
};

export function NotificationHandler({ children }: NotificationHandlerProps) {
  const { user } = useAuth();
  const { permissionStatus, registerAsync, error, isDevice, pushToken } = useExpoPushNotifications();

  const [showFallbackUI, setShowFallbackUI] = React.useState(false);

  // Log notification handler state on mount and updates
  useEffect(() => {
    logNotificationHandler('State update', {
      userId: user?.id,
      isDevice,
      permissionStatus,
      pushToken,
      showFallbackUI,
      error,
    });
  }, [user?.id, isDevice, permissionStatus, pushToken, showFallbackUI, error]);

  const responseListenerRef = useRef<any>(null);
  const receivedListenerRef = useRef<any>(null);
  const lastPromptAtRef = useRef<number | null>(null);

  const detachNotificationListener = useCallback(
    (subscription: any) => {
      if (!subscription || Platform.OS === 'web') {
        return;
      }
      if (typeof subscription.remove === 'function') {
        subscription.remove();
      } else if (
        Notifications &&
        typeof Notifications.removeNotificationSubscription === 'function'
      ) {
        Notifications.removeNotificationSubscription(subscription);
      }
    },
    []
  );

  const handleNotificationResponse = useCallback((response: any) => {
    if (!response?.notification?.request?.content) return;
    
    const data = normalizeData(response.notification.request.content.data);
    logNotificationHandler('Notification tapped', {
      title: response.notification.request.content.title,
      body: response.notification.request.content.body,
      data,
      actionIdentifier: response.actionIdentifier,
      trigger: response.notification.request.trigger,
    });
    navigateFromNotification(data);
  }, []);

  // Removed automatic notification permission prompts
  // Permissions should only be requested during tutorial or when user explicitly enables in settings
  // This function is kept for potential future use but is not automatically called
  const maybePromptForPermissions = useCallback(() => {
    if (!user?.id || !isDevice) {
      return;
    }

    if (!permissionStatus) {
      return;
    }

    const grantedStatus = Platform.OS === 'web' ? 'granted' : PermissionStatus.GRANTED;
    
    if (permissionStatus === grantedStatus) {
      if (showFallbackUI) {
        setShowFallbackUI(false);
      }
      return;
    }

    if (!showFallbackUI) {
      setShowFallbackUI(true);
    }

    const now = Date.now();
    if (lastPromptAtRef.current && now - lastPromptAtRef.current < PROMPT_COOLDOWN_MS) {
      return;
    }

    lastPromptAtRef.current = now;

    showAlert(
      'Enable Notifications',
      'Turn on notifications to get order updates, delivery status, and messages from sellers in real time.',
      [
        { text: 'Not now', style: 'cancel' },
        {
          text: 'Enable',
          onPress: () => {
            if (user?.id) {
              registerAsync(user.id).catch((err) => {
                console.warn('Failed to register for notifications after prompt:', err);
              });
            }
          },
        },
      ],
      { cancelable: true }
    );
  }, [isDevice, permissionStatus, registerAsync, user?.id, showFallbackUI]);

  // REMOVED: Automatic prompt after 2 seconds - only prompt during tutorial or in settings

  useEffect(() => {
    const grantedStatus = Platform.OS === 'web' ? 'granted' : PermissionStatus.GRANTED;
    
    if (permissionStatus === grantedStatus) {
      if (showFallbackUI) {
        setShowFallbackUI(false);
      }
    } else if (permissionStatus) {
      setShowFallbackUI(true);
    }
  }, [permissionStatus, showFallbackUI]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    // Skip notification listeners on web
    if (Platform.OS === 'web' || !Notifications) {
      return;
    }

    let isMounted = true;

    Notifications.getLastNotificationResponseAsync()
      .then((response: any) => {
        if (isMounted && response) {
          handleNotificationResponse(response);
        }
      })
      .catch((err: Error) => {
        console.warn('Failed to get last notification response:', err);
      });

    responseListenerRef.current = Notifications.addNotificationResponseReceivedListener(handleNotificationResponse);
    receivedListenerRef.current = Notifications.addNotificationReceivedListener((notification: any) => {
      const { title, body, data } = notification.request.content;
      
      logNotificationHandler('Notification RECEIVED in foreground', {
        title,
        body,
        data: normalizeData(data),
        trigger: notification.request.trigger,
        identifier: notification.request.identifier,
        showFallbackUI,
      });
      
      if (showFallbackUI) {
        Toast.show({
          type: 'info',
          text1: title || 'New notification',
          text2: body || 'Open to view details',
          visibilityTime: 4000,
          autoHide: true,
          onPress: () => navigateFromNotification(normalizeData(data)),
        });
      }
    });

    // REMOVED: Automatic prompt on app state change - only prompt during tutorial or in settings
    const appStateListener = AppState.addEventListener('change', (nextState: AppStateStatus) => {
      // No automatic permission prompts
    });

    return () => {
      isMounted = false;
      appStateListener.remove();
      detachNotificationListener(responseListenerRef.current);
      responseListenerRef.current = null;
      detachNotificationListener(receivedListenerRef.current);
      receivedListenerRef.current = null;
    };
  }, [handleNotificationResponse, maybePromptForPermissions, showFallbackUI, user?.id, detachNotificationListener]);

  useEffect(() => {
    if (error) {
      console.warn('[NotificationHandler] Push notifications error:', error);
      // Only show toast on native, on web just log
      if (Platform.OS !== 'web') {
      Toast.show({
        type: 'error',
        text1: 'Notification setup issue',
        text2: error,
        visibilityTime: 4000,
      });
      }
    }
  }, [error]);

  return (
    <>
      <PushNotificationsManager />
      {children}
    </>
  );
}

export default NotificationHandler;
